import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../../api/endpoints';
import { useAuthStore } from '../../stores/auth.store';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import type { User } from '@motra/shared';

export default function UserManagement() {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);
  const queryClient = useQueryClient();

  const { data: usersRes, isLoading } = useQuery({
    queryKey: ['users', account?.id],
    queryFn: () => usersApi.list(account!.id),
    enabled: !!account?.id,
  });

  const users = usersRes?.data ?? [];

  const upsertUser = useMutation({
    mutationFn: (data: Partial<User>) => usersApi.upsert(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const deleteUser = useMutation({
    mutationFn: (userId: string) => usersApi.delete(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [attendanceUser, setAttendanceUser] = useState<User | null>(null);

  const [form, setForm] = useState<Partial<User> & { password?: string }>({});
  const [formError, setFormError] = useState('');

  function openEdit(user: User | null) {
    setEditUser(user);
    setForm(user ? { ...user } : { admin: 0 });
    setFormError('');
    setModalOpen(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');

    try {
      await upsertUser.mutateAsync(form);
      setModalOpen(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  const columns = [
    { header: 'Username', accessor: 'username' as keyof User },
    {
      header: 'Role',
      accessor: 'admin' as keyof User,
      render: (val: unknown) => {
        const v = Number(val);
        return v === 2 ? 'Super Admin' : v === 1 ? 'Admin' : 'Staff';
      },
    },
    { header: 'Shop', accessor: 'shop_id' as keyof User },
    { header: 'Phone', accessor: 'phone' as keyof User },
    {
      header: 'Actions',
      accessor: 'id' as keyof User,
      render: (_v: unknown, row: User) => (
        <div className="action-btns">
          <button
            className="btn btn--sm btn--outline"
            onClick={(e) => { e.stopPropagation(); openEdit(row); }}
          >
            {t('edit', 'Edit')}
          </button>
          <button
            className="btn btn--sm btn--outline"
            onClick={(e) => { e.stopPropagation(); setAttendanceUser(row); }}
          >
            {t('attendance', 'Attendance')}
          </button>
          <button
            className="btn btn--sm btn--danger"
            onClick={(e) => {
              e.stopPropagation();
              if (confirm('Delete this user?')) deleteUser.mutate(row.id);
            }}
          >
            {t('delete', 'Delete')}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t('users', 'Users')}</h1>
        <button className="btn btn--primary" onClick={() => openEdit(null)}>
          + {t('add', 'Add User')}
        </button>
      </div>

      <DataTable
        columns={columns}
        data={users}
        loading={isLoading}
        rowKey={(r) => r.id}
        emptyMessage="No users"
      />

      {/* User form modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editUser ? 'Edit User' : 'Add User'}
      >
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label className="form-label">Username *</label>
            <input
              type="text"
              className="form-input"
              value={form.username ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
              required
            />
          </div>
          {!editUser && (
            <div className="form-group">
              <label className="form-label">Password *</label>
              <input
                type="password"
                className="form-input"
                value={(form as { password?: string }).password ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                required
              />
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Role</label>
            <select
              className="form-select"
              value={form.admin ?? 0}
              onChange={(e) => setForm((p) => ({ ...p, admin: parseInt(e.target.value) }))}
            >
              <option value={0}>Staff</option>
              <option value={1}>Admin</option>
              <option value={2}>Super Admin</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              type="text"
              className="form-input"
              value={form.phone ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Language</label>
            <select
              className="form-select"
              value={form.lang ?? 'en'}
              onChange={(e) => setForm((p) => ({ ...p, lang: e.target.value }))}
            >
              <option value="en">English</option>
              <option value="zh">中文</option>
              <option value="th">ภาษาไทย</option>
              <option value="vi">Tiếng Việt</option>
            </select>
          </div>

          {formError && <div className="alert alert--error">{formError}</div>}

          <div className="form-actions">
            <button type="button" className="btn btn--outline" onClick={() => setModalOpen(false)}>
              {t('cancel', 'Cancel')}
            </button>
            <button type="submit" className="btn btn--primary" disabled={upsertUser.isPending}>
              {upsertUser.isPending ? 'Saving...' : t('save', 'Save')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Attendance modal */}
      <Modal
        open={!!attendanceUser}
        onClose={() => setAttendanceUser(null)}
        title={`Attendance - ${attendanceUser?.username}`}
        size="lg"
      >
        <p className="text-muted">Attendance records for {attendanceUser?.username}</p>
        {/* Attendance table would be populated via usersApi.attendance() */}
        <div className="alert alert--info">
          Attendance data loaded from /users/{attendanceUser?.id}/attendance
        </div>
      </Modal>
    </div>
  );
}
